import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { useStyle } from "../../../Design/useStyle";
import { defaultStyles } from "./defaultStyles";
import defaultPlaceholderImage from "./img_preview_large.png";

interface ImageProps extends Omit<ComponentPropsWithRef<"img">, "loading"> {
  alt?: string;
  image: {
    signed_id: string;
    placeholder?: string;
  };
  loading?: "lazy" | "eager";
  styles?: Record<string, StyleValue>;
}

export const Img = forwardRef<HTMLImageElement, ImageProps>(
  ({ image, styles = {}, alt = "", loading = "lazy", ...rest }, ref) => {
    const inlineStyles = {
      ...parseStyles({
        styles: omit(styles, "sizing"),
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
    };
    const styled = useStyle(() => ({
      base: {
        height: "auto",
        maxHeight: "384px",
        width: "100%",
        maxWidth: "100%",
        objectFit: "cover",
        opacity: 1,
      },
    }));

    const attachmentMetadata = useAttachmentMetadata(image.signed_id);
    const { url } = attachmentMetadata ?? {};
    const placeholder = image.placeholder
      ? image.placeholder
      : defaultPlaceholderImage;

    if (!url) {
      return (
        <img
          {...rest}
          ref={ref}
          src={placeholder}
          alt={alt}
          style={styled({ style: inlineStyles })}
          loading={loading}
        />
      );
    }

    return (
      <img
        {...rest}
        ref={ref}
        src={url}
        alt={alt}
        style={styled({ style: inlineStyles })}
        loading={loading}
      />
    );
  },
);

Img.displayName = "Img";
